<template>
  <div class="employees-mobile-container py-2">
    <!-- Mobile Employee Cards List -->
    <div v-if="parentPage.employees && parentPage.employees.length > 0">
      <v-card
        v-for="employee in parentPage.employees"
        :key="employee._id"
        outlined
        class="employee-card mb-4 rounded-xl shadow-sm border border-gray-100 overflow-hidden"
      >
        <!-- Card Header: Avatar, Name, Status -->
        <v-card-text class="pb-2 pt-4 px-4 bg-gray-50 flex justify-between items-start">
            <UserAvatar
              :user="{ ...employee, profileImage: employee.profile_image }"
              :size="44"
              :show-name="true"
              :tooltip-enabled="false"
              :show-mask-details="false"
              name-class="text-sm font-bold text-gray-800"
            >
              <template v-slot:additional-content>
                <div class="text-xs text-gray-500 font-semibold mt-0.5">
                  ID: {{ employee.employeeId || '-' }}
                </div>
              </template>
            </UserAvatar>

          <span :class="parentPage.getPartnerStatusClr(employee.systemUserId?.status) + ' text-xs px-2 py-0.5 rounded bg-white border'">
            {{ parentPage.convertToTitleCase(employee.systemUserId?.status) }}
          </span>
        </v-card-text>

        <!-- Card Body: Structural Details -->
        <v-card-text class="px-4 py-3 border-t">
          <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div>
              <span class="text-gray-400 block">Department</span>
              <span class="font-bold text-gray-700">{{ employee.department?.departmentName || '-' }}</span>
            </div>
            <div>
              <span class="text-gray-400 block">Designation</span>
              <span class="font-bold text-gray-700">{{ employee.designation?.designationName || '-' }}</span>
            </div>
            <div>
              <span class="text-gray-400 block">Branch</span>
              <span class="font-bold text-gray-700">{{ employee.branch ? parentPage.findBranch(employee)?.branchName : '-' }}</span>
            </div>
            <div>
              <span class="text-gray-400 block">Team</span>
              <span class="font-bold text-gray-700">{{ employee.team ? employee.team?.teamName : '-' }}</span>
            </div>
          </div>

          <v-divider class="my-3"></v-divider>

          <!-- System Assignment Dropdowns -->
          <div class="flex flex-col gap-2">
            <!-- Role Selector -->
            <div>
              <label class="text-[10px] uppercase font-bold text-gray-400 block mb-1">Access Role</label>
              <v-autocomplete
                hide-details
                solo
                flat
                outlined
                dense
                :readonly="!parentPage.hasIamPermission(parentPage.iamStatements, 'organization.employees.update')"
                placeholder="No Role"
                v-model="employee.wfmRole"
                :items="parentPage.getRoleDropdown(employee)"
                item-text="role"
                item-value="_id"
                class="mobile-selector"
                :menu-props="{ offsetY: true }"
                @change="parentPage.updateRole(employee)"
                @click="parentPage.newRoleId(employee)"
              ></v-autocomplete>
            </div>

            <!-- Manager Selector -->
            <div>
              <label class="text-[10px] uppercase font-bold text-gray-400 block mb-1">Direct Manager</label>
              <v-autocomplete
                hide-details
                solo
                flat
                outlined
                dense
                placeholder="No Manager"
                :readonly="!parentPage.hasIamPermission(parentPage.iamStatements, 'organization.employees.update')"
                v-model="employee.reportesTo"
                :items="parentPage.getSelectItems(employee)"
                :item-text="parentPage.getManagerFullName"
                item-value="_id"
                @change="parentPage.assignToManager(employee)"
                class="mobile-selector"
                :menu-props="{ offsetY: true }"
              >
                <template v-slot:item="{ item: managerItem }">
                  <div v-if="managerItem._id !== null" class="d-flex align-center py-1">
                    <UserAvatar
                      :user="managerItem"
                      :size="20"
                      :show-name="true"
                      :name-class="'text-xs ml-2'"
                      :tooltip-enabled="false"
                    />
                  </div>
                  <div v-else class="d-flex align-center py-1">
                    <span class="ml-2 text-xs">Unassigned (No Role)</span>
                  </div>
                </template>
              </v-autocomplete>
            </div>
          </div>
        </v-card-text>

        <!-- Card Footer: Quick Actions Row -->
        <v-card-actions class="px-4 py-2 bg-gray-50 border-t flex justify-end gap-1 flex-wrap">
          <!-- Live Location -->
          <v-btn
            icon
            small
            outlined
            color="primary"
            class="rounded-lg border-gray-300"
            @click="parentPage.openMap(employee)"
            v-if="parentPage.isFeatureVisible('EMPLOYEE_LIVE_TRACKING')"
          >
            <v-icon size="16">mdi-map-marker</v-icon>
          </v-btn>

          <!-- Leave Actions -->
          <v-btn
            icon
            small
            outlined
            color="orange darken-2"
            class="rounded-lg border-gray-300"
            @click="parentPage.userLeaves(employee)"
            v-if="parentPage.hasIamPermission(parentPage.iamStatements, 'organization.employees.update')"
          >
            <v-icon size="16">mdi-calendar-alert</v-icon>
          </v-btn>

          <!-- Payroll slip -->
          <v-btn
            icon
            small
            outlined
            color="green darken-2"
            class="rounded-lg border-gray-300"
            @click="parentPage.addUserExpense(employee)"
            v-if="parentPage.hasIamPermission(parentPage.iamStatements, 'organization.employees.update')"
          >
            <v-icon size="16">mdi-cash-multiple</v-icon>
          </v-btn>

          <!-- Digital Profile -->
          <v-btn
            icon
            small
            outlined
            color="purple darken-2"
            class="rounded-lg border-gray-300"
            @click="parentPage.showDigitalProfile(employee)"
            v-if="parentPage.hasIamPermission(parentPage.iamStatements, 'organization.employees.update')"
          >
            <v-icon size="16">mdi-card-account-details-outline</v-icon>
          </v-btn>

          <!-- Edit Profile -->
          <v-btn
            small
            depressed
            color="primary"
            class="white--text rounded-lg px-3 font-semibold normal-case text-xs"
            @click="parentPage.editUser(employee)"
            v-if="parentPage.hasIamPermission(parentPage.iamStatements, 'organization.employees.update')"
          >
            <v-icon left size="14">mdi-pencil</v-icon>
            Edit
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Pagination Control -->
      <div class="flex justify-center mt-4">
        <v-pagination
          v-model="parentPage.options.page"
          :length="totalPages"
          total-visible="5"
          :color="$vuetify.theme.currentTheme.primary"
        ></v-pagination>
      </div>
    </div>

    <!-- No Data Placeholder -->
    <no-data title="Nothing to Display" subTitle="You need to import the employees" v-else>
      <v-btn
        @click="parentPage.importEmployees"
        :color="$vuetify.theme.currentTheme.primary"
        :loading="parentPage.loadingImport"
        :disabled="parentPage.loadingImport"
        class="white--text"
        v-show="parentPage.hasIamPermission(parentPage.iamStatements, 'organization.employees.create')"
      >
        Import Employees
        <v-icon right dark> mdi-download </v-icon>
      </v-btn>
    </no-data>
  </div>
</template>

<script>
import UserAvatar from '@/components/UserAvatar.vue'
import NoData from '@/components/NoData.vue'

export default {
  name: 'EmployeesPage_mobile',
  components: {
    UserAvatar,
    NoData
  },
  inject: ['parentPage'],
  computed: {
    totalPages() {
      const limit = this.parentPage.options.itemsPerPage || 10
      return Math.ceil(this.parentPage.totalEmployees / limit) || 1
    }
  }
}
</script>

<style scoped>
.mobile-selector >>> .v-input__control > .v-input__slot {
  min-height: 36px !important;
  font-size: 12px !important;
}

.employee-card {
  border-radius: 12px !important;
}
</style>
